// The pref ledger (#1858): prefs are global (`pref:<key>`), not the tutorial
// project's, so the tutorial undoes its own writes. Each write is recorded as
// `{ key, before, written }` — the first `before` wins when a key is written
// again — and when the unit ends a key goes back to `before` only if it still
// holds `written`. Anything else is the user's change, and stays.
//
// Accepted imprecision: a pref the user toggled away and back reads as untouched.
//
// A step can ask the user to set the very pref the setup wrote (Structure's
// «show the navigator»), so the value-compare reads the step's change as the
// user's and keeps it. A unit that finishes therefore asks about each such
// step-requested key left different from `before`; a key no step asked for
// stays the user's.
//
// Only boolean prefs: every pref a chapter sets up is a switch.

import { PREFERENCE_DEFAULTS, type PreferenceSchema } from '@/preferences';

export type BooleanPrefKey = { [K in keyof PreferenceSchema]: PreferenceSchema[K] extends boolean ? K : never }[keyof PreferenceSchema];

export interface PrefLedgerEntry {
    key: BooleanPrefKey;
    /** The value before the tutorial's first write. */
    before: boolean;
    /** The tutorial's last write. */
    written: boolean;
}

export type PrefLedger = readonly PrefLedgerEntry[];

export function recordPrefWrite(ledger: PrefLedger, key: BooleanPrefKey, before: boolean, written: boolean): PrefLedger {
    const existing = ledger.find(entry => entry.key === key);
    if (existing === undefined) return [...ledger, { key, before, written }];
    return ledger.map(entry => (entry === existing ? { ...entry, written } : entry));
}

/** What the end of a unit puts back: each key that still holds what the tutorial wrote. */
export function prefsToRestore(ledger: PrefLedger, read: (key: BooleanPrefKey) => boolean): { key: BooleanPrefKey; value: boolean }[] {
    return ledger.filter(entry => read(entry.key) === entry.written).map(entry => ({ key: entry.key, value: entry.before }));
}

/** A pref a finished unit asks about, and what «Вернуть как было» puts back. */
export interface PrefToKeep {
    key: BooleanPrefKey;
    before: boolean;
}

/** A finished unit's end: what goes back without asking, and the
 *  step-requested keys left different from before — for the user to keep or
 *  revert. */
export function endOfFinishedUnit(ledger: PrefLedger, read: (key: BooleanPrefKey) => boolean, asked: ReadonlySet<BooleanPrefKey>) {
    const restore = prefsToRestore(ledger, read);
    const ask = ledger
        .filter(entry => asked.has(entry.key) && read(entry.key) !== entry.before && !restore.some(r => r.key === entry.key))
        .map((entry): PrefToKeep => ({ key: entry.key, before: entry.before }));
    return { restore, ask };
}

const isBooleanPrefKey = (key: unknown): key is BooleanPrefKey =>
    typeof key === 'string' && Object.entries(PREFERENCE_DEFAULTS).some(([name, value]) => name === key && typeof value === 'boolean');

/** A stored ledger, keeping only the entries this client can restore. */
export function parsePrefLedger(raw: unknown): PrefLedger {
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((entry): PrefLedgerEntry[] => {
        if (typeof entry !== 'object' || entry === null) return [];
        const { key, before, written } = entry;
        return isBooleanPrefKey(key) && typeof before === 'boolean' && typeof written === 'boolean' ? [{ key, before, written }] : [];
    });
}
